// LibraryExplorerView — admin da Biblioteca de Provas em estilo File Explorer.
//
// Layout 2 painéis:
//  - Esquerda: árvore de pastas (colapsável) + pasta virtual "Sem pasta"
//  - Direita: conteúdo da pasta selecionada (sub-pastas + bancos)
//  - Breadcrumb: caminho da pasta selecionada
//
// Ações principais:
//  - Nova pasta (raiz ou sub-pasta)
//  - Editar pasta (renomear, mover, defaultVisibility com cascata)
//  - Apagar pasta (banks viram órfãos, FK ON DELETE SET NULL)
//  - "Adicionar bancos a esta pasta" —  modal picker filtrável
//  - Multi-select bancos + "Mover N para⬦" —  dropdown de pastas
//  - Toggle "lista admin avançado" volta para tabela tradicional (LibraryFoldersView)

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowRight,
  ChevronRight,
  Columns2,
  Edit3,
  Eye,
  EyeOff,
  FilePenLine,
  FileText,
  Filter,
  Folder,
  FolderOpen,
  FolderPlus,
  Globe,
  Grid3x3,
  Home,
  Inbox,
  Library,
  Route,
  List,
  Loader2,
  Lock,
  Move,
  Plus,
  Redo2,
  Search,
  Trash2,
  Undo2,
  X
} from "lucide-react";
import { api, ApiError, type LibraryFolderRow } from "./api";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

// ---------- estado global da view ----------
const folders = signal<LibraryFolderRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
/** ID da pasta selecionada. null = raiz (mostra todas as pastas-raiz). "none" = pasta virtual de órfãos. */
const selectedFolderId = signal<string | null>(null);
/** IDs das pastas expandidas na árvore (UX como Windows: clicar abre filhos). */
const expanded = signal<Set<string>>(new Set());

// ---------- undo stack ----------
type UndoEntry =
  | { kind: "bank-move"; fromMap: Record<string, string | null>; toFolderId: string | null; summary: string }
  | { kind: "folder-move"; folderId: string; fromParentId: string | null; toParentId: string | null; summary: string }
  | { kind: "folder-rename"; folderId: string; oldName: string; newName: string; summary: string }
  | { kind: "folder-create"; folderId: string; summary: string };

const undoStack = signal<UndoEntry[]>([]);
const redoStack = signal<UndoEntry[]>([]);
const UNDO_MAX = 30;

/** Registra uma mudança no stack de undo. Limpa o redoStack porque uma nova
 *  ação invalida o histórico de redo (sequência divergiu). */
function pushUndo(entry: UndoEntry) {
  const next = [...undoStack.value, entry];
  while (next.length > UNDO_MAX) next.shift();
  undoStack.value = next;
  if (redoStack.value.length > 0) redoStack.value = [];
}

/** Aplica a operação na direção inversa (undo) e devolve true se conseguiu.
 *  Não toca nos stacks — quem chama decide para qual deles empurrar. */
async function applyInverse(entry: UndoEntry): Promise<boolean> {
  switch (entry.kind) {
    case "bank-move": {
      const byPrev = new Map<string | null, string[]>();
      for (const [id, prev] of Object.entries(entry.fromMap)) {
        if (!byPrev.has(prev)) byPrev.set(prev, []);
        byPrev.get(prev)!.push(id);
      }
      for (const [prev, ids] of byPrev) {
        await api.bulkAssignLibraryBanks(prev, ids);
      }
      return true;
    }
    case "folder-move":
      await api.patchLibraryFolder(entry.folderId, { parentId: entry.fromParentId });
      return true;
    case "folder-rename":
      await api.patchLibraryFolder(entry.folderId, { name: entry.oldName });
      return true;
    case "folder-create":
      await api.deleteLibraryFolder(entry.folderId);
      if (selectedFolderId.value === entry.folderId) selectedFolderId.value = null;
      return true;
  }
}

/** Aplica a operação na direção original (redo). Para folder-create, retorna
 *  false porque um folder apagado por undo perdeu o id original — refazer
 *  exigiria criar uma pasta nova com id novo, quebrando entries seguintes. */
async function applyForward(entry: UndoEntry): Promise<boolean> {
  switch (entry.kind) {
    case "bank-move": {
      const ids = Object.keys(entry.fromMap);
      await api.bulkAssignLibraryBanks(entry.toFolderId, ids);
      return true;
    }
    case "folder-move":
      await api.patchLibraryFolder(entry.folderId, { parentId: entry.toParentId });
      return true;
    case "folder-rename":
      await api.patchLibraryFolder(entry.folderId, { name: entry.newName });
      return true;
    case "folder-create":
      // Não dá para refazer criação (perderia o id). Skip silencioso.
      return false;
  }
}

async function refreshAfterMutation() {
  await loadFolders();
  const sel = selectedFolderId.value;
  if (sel === "none") await loadBanksForFolder(null);
  else if (sel) await loadBanksForFolder(sel);
}

async function doUndo() {
  const stack = undoStack.value;
  if (stack.length === 0) return;
  const last = stack[stack.length - 1];
  undoStack.value = stack.slice(0, -1);
  try {
    await applyInverse(last);
    redoStack.value = [...redoStack.value, last];
    await refreshAfterMutation();
  } catch (err) {
    undoStack.value = [...undoStack.value, last];
    void openAlert({ title: "Falha ao desfazer", message: friendlyError(err) });
  }
}

async function doRedo() {
  const stack = redoStack.value;
  if (stack.length === 0) return;
  const next = stack[stack.length - 1];
  redoStack.value = stack.slice(0, -1);
  try {
    const ok = await applyForward(next);
    if (ok) {
      undoStack.value = [...undoStack.value, next];
    }
    await refreshAfterMutation();
  } catch (err) {
    redoStack.value = [...redoStack.value, next];
    void openAlert({ title: "Falha ao refazer", message: friendlyError(err) });
  }
}

type BankInFolder = {
  id: string;
  name: string;
  description: string | null;
  companyId: string | null;
  companyName: string | null;
  visibility: "private" | "unlisted" | "public";
  libraryFolderId: string | null;
  archivedAt: string | null;
  updatedAt: string;
};

type EssayInFolder = {
  id: string;
  slug: string;
  title: string;
  displayName: string;
  banca: string;
  year: number | null;
  phase: string | null;
  edition: string | null;
  visibility: "private" | "unlisted" | "public";
  status: "draft" | "published" | "archived";
  libraryFolderId: string | null;
  archivedAt: string | null;
  updatedAt: string;
};

const banks = signal<BankInFolder[]>([]);
const banksPhase = signal<"loading" | "ready" | "error" | "idle">("idle");
const banksErr = signal<string | null>(null);
const essays = signal<EssayInFolder[]>([]);
const essaysPhase = signal<"loading" | "ready" | "error" | "idle">("idle");
const essaysErr = signal<string | null>(null);
/** Filtro "Só públicos" no painel de órfãos (compartilhado entre painéis). */
const onlyPublic = signal<boolean>(false);

// ---------- painel secundário (modo split / "painel duplo") ----------
/** Quando true, mostra um segundo painel à direita do principal. Permite
 *  arrastar bancos/pastas entre painas distantes sem ter que navegar antes. */
const splitMode = signal<boolean>(false);
/** Painel ativo — clique na árvore esquerda navega esse aqui. */
const activePane = signal<"primary" | "secondary">("primary");
const selectedFolderIdB = signal<string | null>(null);
const banksB = signal<BankInFolder[]>([]);
const banksBPhase = signal<"loading" | "ready" | "error" | "idle">("idle");
const banksBErr = signal<string | null>(null);

async function loadFolders() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listLibraryFolders();
    folders.value = r.folders;
    phase.value = "ready";
  } catch (err) {
    errMsg.value = err instanceof ApiError ? err.message : String(err);
    phase.value = "error";
  }
}

async function loadBanksForFolder(folderId: string | null) {
  banksPhase.value = "loading";
  banksErr.value = null;
  try {
    const key = folderId === null ? "none" : folderId;
    const r = await api.listLibraryBanksInFolder(key);
    banks.value = r.banks;
    banksPhase.value = "ready";
  } catch (err) {
    banksErr.value = friendlyError(err, { fallback: "Falha ao carregar bancos." });
    banksPhase.value = "error";
  }
  // Carrega redações em paralelo (mesma pasta) — única biblioteca, dois tipos
  // de itens. UI mostra os dois listados.
  void loadEssaysForFolder(folderId);
}

async function loadEssaysForFolder(folderId: string | null) {
  essaysPhase.value = "loading";
  essaysErr.value = null;
  try {
    const key = folderId === null ? "none" : folderId;
    const r = await api.listLibraryEssaysInFolder(key);
    essays.value = r.essays;
    essaysPhase.value = "ready";
  } catch (err) {
    essaysErr.value = friendlyError(err, { fallback: "Falha ao carregar redações." });
    essaysPhase.value = "error";
  }
}

async function loadBanksForFolderB(folderId: string | null) {
  banksBPhase.value = "loading";
  banksBErr.value = null;
  try {
    const key = folderId === null ? "none" : folderId;
    const r = await api.listLibraryBanksInFolder(key);
    banksB.value = r.banks;
    banksBPhase.value = "ready";
  } catch (err) {
    banksBErr.value = friendlyError(err, { fallback: "Falha ao carregar bancos." });
    banksBPhase.value = "error";
  }
}

/** Acessores para o painel ativo (A ou B). Centraliza qual signal usar
 *  dependendo de qual painel o usuário tocou por último. */
function getPaneSignals(paneId: "primary" | "secondary") {
  if (paneId === "secondary") {
    return {
      selectedFolderId: selectedFolderIdB,
      banks: banksB,
      banksPhase: banksBPhase,
      banksErr: banksBErr,
      load: loadBanksForFolderB
    } as const;
  }
  return {
    selectedFolderId,
    banks,
    banksPhase,
    banksErr,
    load: loadBanksForFolder
  } as const;
}

/** Atalho usado pelo refreshAfterMutation: recarrega os dois painéis. */
async function refreshActivePanes() {
  const a = selectedFolderId.value;
  if (a === "none") await loadBanksForFolder(null);
  else if (a) await loadBanksForFolder(a);
  if (splitMode.value) {
    const b = selectedFolderIdB.value;
    if (b === "none") await loadBanksForFolderB(null);
    else if (b) await loadBanksForFolderB(b);
  }
}

export function LibraryExplorerView() {
  useEffect(() => {
    void loadFolders();
  }, []);

  // Sempre que a pasta selecionada muda, recarrega bancos.
  useEffect(() => {
    const sel = selectedFolderId.value;
    if (sel === null) {
      // Raiz: não carrega banks (mostra só a árvore + dica).
      banks.value = [];
      banksPhase.value = "idle";
      return;
    }
    void loadBanksForFolder(sel === "none" ? null : sel);
  }, [selectedFolderId.value]);

  // Atalhos de teclado: Ctrl+Z (desfazer) / Ctrl+Y ou Ctrl+Shift+Z (refazer).
  // Ignora quando o foco está em input/textarea/contenteditable.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) return;
      }
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        void doUndo();
      } else if (key === "y" || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        void doRedo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="space-y-3">
      <ExplorerTopbar />

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando pastas⬦
        </p>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mr-2 inline size-4" />
          {errMsg.value}
        </div>
      )}

      {phase.value === "ready" && (
        <div
          class={
            splitMode.value
              ? "grid grid-cols-1 gap-3 md:grid-cols-[260px_1fr_1fr]"
              : "grid grid-cols-1 gap-3 md:grid-cols-[280px_1fr]"
          }
        >
          <FolderTreePanel />
          <FolderContentPanel />
          {splitMode.value && <SecondPane />}
        </div>
      )}

      <UndoHistoryBar />
    </div>
  );
}

// ============================================================================
// ExplorerTopbar — barra superior com os botões Desfazer/Refazer + toggle
// "Painel duplo" para abrir uma segunda janela à direita (estilo Total
// Commander), facilitando arrastar bancos entre pastas distantes.
// ============================================================================

function ExplorerTopbar() {
  const u = undoStack.value;
  const r = redoStack.value;
  return (
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => void doUndo()}
          disabled={u.length === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-40"
          title={u.length > 0 ? `Desfazer: ${u[u.length - 1].summary} (Ctrl+Z)` : "Nada para desfazer"}
          aria-label="Desfazer (Ctrl+Z)"
        >
          <Undo2 className="size-3.5" />
          Desfazer
          <kbd class="ml-1 hidden rounded bg-muted px-1 font-mono text-[9px] text-muted-foreground md:inline">
            Ctrl+Z
          </kbd>
        </button>
        <button
          type="button"
          onClick={() => void doRedo()}
          disabled={r.length === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-40"
          title={r.length > 0 ? `Refazer: ${r[r.length - 1].summary} (Ctrl+Y)` : "Nada para refazer"}
          aria-label="Refazer (Ctrl+Y)"
        >
          <Redo2 className="size-3.5" />
          Refazer
          <kbd class="ml-1 hidden rounded bg-muted px-1 font-mono text-[9px] text-muted-foreground md:inline">
            Ctrl+Y
          </kbd>
        </button>
      </div>
      <button
        type="button"
        onClick={() => {
          splitMode.value = !splitMode.value;
          if (!splitMode.value) {
            // Reseta painel B ao fechar.
            selectedFolderIdB.value = null;
            banksB.value = [];
            banksBPhase.value = "idle";
            banksBErr.value = null;
            activePane.value = "primary";
          }
        }}
        class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
          splitMode.value
            ? "border-primary/40 bg-primary/10 text-primary"
            : "border-border bg-background hover:bg-accent"
        }`}
        title="Abre um segundo painel à direita para arrastar bancos entre pastas distantes"
      >
        <Columns2 className="size-3.5" />
        Painel duplo
      </button>
    </div>
  );
}

// ============================================================================
// SecondPane — segundo painel à direita no modo "Painel duplo". Tem seu
// próprio folder picker (dropdown) e lista bancos da pasta escolhida. Aceita
// drops dos painéis A/B (drag&drop é cross-pane porque os handlers leem o
// targetFolderId do elemento alvo).
// ============================================================================

function SecondPane() {
  const folderId = selectedFolderIdB.value;
  // Carrega bancos quando seleção muda
  useEffect(() => {
    const id = selectedFolderIdB.value;
    if (id === null) {
      banksB.value = [];
      banksBPhase.value = "idle";
      return;
    }
    void loadBanksForFolderB(id === "none" ? null : id);
  }, [selectedFolderIdB.value]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = window.localStorage.getItem("library.viewMode");
    return saved === "grid" ? "grid" : "list";
  });

  useEffect(() => {
    setSelected(new Set());
  }, [folderId]);

  function toggleBank(id: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  }
  function toggleAll(checked: boolean) {
    if (!checked) setSelected(new Set());
    else setSelected(new Set(banksB.value.map((b) => b.id)));
  }

  const isActive = activePane.value === "secondary";
  const folder = folderId && folderId !== "none"
    ? folders.value.find((f) => f.id === folderId) ?? null
    : null;

  return (
    <section
      onClick={() => (activePane.value = "secondary")}
      class={`flex min-h-[400px] flex-col rounded-md border bg-card/40 transition-colors ${
        isActive ? "border-primary/60 ring-1 ring-primary/40" : "border-border"
      }`}
      title={isActive ? "Painel ativo — clique na árvore para navegar aqui" : "Clique para ativar este painel"}
    >
      <header class="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Painel 2
          </span>
          <select
            value={folderId ?? ""}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              selectedFolderIdB.value = v === "" ? null : v;
              activePane.value = "secondary";
            }}
            class="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs focus:border-primary focus:outline-none"
            aria-label="Pasta exibida no painel 2"
            title="Pasta exibida no painel 2"
          >
            <option value="">Escolha uma pasta⬦</option>
            <option value="none">📥 Sem pasta (órfãos)</option>
            {orderedHierarchy(folders.value).map(({ folder: f, depth }) => (
              <option key={f.id} value={f.id}>
                {"  ".repeat(depth)}
                {f.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      {folderId === null && (
        <div class="grid flex-1 place-items-center px-4 py-12 text-center">
          <div>
            <FolderOpen className="mx-auto mb-2 size-8 text-muted-foreground/40" />
            <p class="text-sm text-muted-foreground">
              Selecione uma pasta acima para abrir aqui.
            </p>
          </div>
        </div>
      )}

      {folderId !== null && (
        <>
          {/* Drop-zone do painel B: arrastar para o painel inteiro move para
              esta pasta (ou para órfãos). */}
          <div
            class="flex flex-1 flex-col"
            onDragOver={(e) => onFolderDragOver(e, folderId === "none" ? "root" : folderId)}
            onDragLeave={onFolderDragLeave}
            onDrop={(e) => void onFolderDrop(e, folderId === "none" ? null : folderId)}
          >
            <BanksToolbar
              totalSelected={selected.size}
              totalShown={banksB.value.length}
              onSelectAll={(c) => toggleAll(c)}
              onMove={() => setMoveOpen(true)}
              onClear={() => setSelected(new Set())}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
            />
            {banksBPhase.value === "loading" && (
              <p class="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Carregando⬦
              </p>
            )}
            {banksBPhase.value === "error" && (
              <p class="px-4 py-6 text-sm text-destructive">
                <AlertCircle className="mr-1 inline size-3" />
                {banksBErr.value}
              </p>
            )}
            {banksBPhase.value === "ready" && banksB.value.length === 0 && (
              <div class="grid flex-1 place-items-center px-4 py-12 text-center">
                <p class="text-sm text-muted-foreground">Pasta vazia.</p>
              </div>
            )}
            {banksBPhase.value === "ready" && banksB.value.length > 0 && (
              <BanksList
                banks={banksB.value}
                selected={selected}
                onToggle={toggleBank}
                viewMode={viewMode}
              />
            )}
          </div>
        </>
      )}

      {moveOpen && selected.size > 0 && (
        <MoveBanksModal
          bankIds={Array.from(selected)}
          fromFolder={folder}
          onClose={() => setMoveOpen(false)}
          onMoved={async () => {
            setMoveOpen(false);
            setSelected(new Set());
            await Promise.all([
              loadFolders(),
              folderId === "none"
                ? loadBanksForFolderB(null)
                : folderId
                ? loadBanksForFolderB(folderId)
                : Promise.resolve()
            ]);
          }}
        />
      )}
    </section>
  );
}

// ============================================================================
// UndoHistoryBar — barra inferior que mostra o histórico recente de mudanças
// e expõe os botões Desfazer (Ctrl+Z) e Refazer (Ctrl+Y).
// ============================================================================

function UndoHistoryBar() {
  const u = undoStack.value;
  const r = redoStack.value;
  if (u.length === 0 && r.length === 0) return null;
  // Mostra as últimas 5 mudanças do undoStack na ordem do mais recente.
  const recent = [...u].slice(-5).reverse();
  return (
    <div class="sticky bottom-0 mt-3 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/95 px-3 py-2 shadow-sm backdrop-blur">
      <div class="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => void doUndo()}
          disabled={u.length === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-40"
          title="Desfazer última ação (Ctrl+Z)"
        >
          <Undo2 className="size-3.5" />
          Desfazer
          <kbd class="ml-1 hidden rounded bg-muted px-1 font-mono text-[9px] text-muted-foreground md:inline">Ctrl+Z</kbd>
        </button>
        <button
          type="button"
          onClick={() => void doRedo()}
          disabled={r.length === 0}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-40"
          title="Refazer última ação desfeita (Ctrl+Y)"
        >
          <Redo2 className="size-3.5" />
          Refazer
          <kbd class="ml-1 hidden rounded bg-muted px-1 font-mono text-[9px] text-muted-foreground md:inline">Ctrl+Y</kbd>
        </button>
      </div>
      <div class="flex flex-1 flex-wrap items-center gap-1 overflow-x-auto text-[11px] text-muted-foreground">
        {recent.length === 0 ? (
          <span class="italic">Nenhuma mudança nesta sessão.</span>
        ) : (
          <>
            <span class="font-semibold">Histórico:</span>
            {recent.map((e, i) => (
              <span
                key={i}
                class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5"
              >
                {i === 0 && <span class="size-1.5 rounded-full bg-emerald-500" aria-hidden="true" />}
                {e.summary}
              </span>
            ))}
          </>
        )}
      </div>
      {u.length > 5 && (
        <span class="text-[10px] text-muted-foreground">
          +{u.length - 5} anteriores
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Painel esquerdo: árvore de pastas
// ============================================================================

function FolderTreePanel() {
  const orphanCount = useMemo(() => {
    // não temos contador exato de órfãos — vai chegar quando entrar nela.
    return null;
  }, []);

  return (
    <aside class="rounded-md border border-border bg-card/40">
      <header class="flex items-center justify-between border-b border-border px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        <span>Pastas</span>
        <span class="font-mono normal-case text-muted-foreground/70">
          {folders.value.length}
        </span>
      </header>
      <div class="max-h-[70vh] overflow-y-auto py-1.5">
        {/* Drop-zone raiz: arrastar pasta aqui remove o parentId. */}
        <div
          onDragOver={(e) => onFolderDragOver(e, "root")}
          onDragLeave={onFolderDragLeave}
          onDrop={(e) => void onFolderDrop(e, null)}
          class="mx-1.5 mb-1 rounded-md px-1.5 py-1 text-[11px] font-semibold text-muted-foreground data-[drop-over=true]:bg-primary/20 data-[drop-over=true]:text-primary"
          title="Arraste uma pasta aqui para tirar a categorização de sub-pasta"
        >
          <button
            type="button"
            onClick={() => (selectedFolderId.value = null)}
            class={`flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left transition-colors ${
              selectedFolderId.value === null ? "bg-primary/10 text-primary" : "hover:text-foreground"
            }`}
          >
            <Library className="size-3.5" />
            Raiz da biblioteca
          </button>
        </div>
        <FolderTreeNode parentId={null} depth={0} />
        {/* Pasta virtual: órfãos (sem pasta) */}
        <button
          type="button"
          onClick={() => (selectedFolderId.value = "none")}
          class={`mt-1 flex w-full items-center gap-1.5 border-t border-border px-3 py-1.5 text-left text-sm transition-colors ${
            selectedFolderId.value === "none"
              ? "bg-amber-500/10 font-semibold text-amber-700 dark:text-amber-300"
              : "text-muted-foreground hover:bg-accent"
          }`}
        >
          <Inbox className="size-4" />
          <span class="flex-1 truncate">Sem pasta (órfãos)</span>
          {orphanCount !== null && (
            <span class="rounded bg-muted px-1.5 text-[10px] font-mono">{orphanCount}</span>
          )}
        </button>
      </div>
    </aside>
  );
}

function FolderTreeNode({
  parentId,
  depth
}: {
  parentId: string | null;
  depth: number;
}) {
  const children = folders.value
    .filter((f) => (f.parentId ?? null) === parentId)
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));
  if (children.length === 0) {
    if (depth === 0) {
      return (
        <p class="px-3 py-4 text-center text-[11px] text-muted-foreground">
          Nenhuma pasta ainda.
        </p>
      );
    }
    return null;
  }
  return (
    <ul class="space-y-px">
      {children.map((f) => (
        <FolderTreeItem key={f.id} folder={f} depth={depth} />
      ))}
    </ul>
  );
}

function FolderTreeItem({
  folder,
  depth
}: {
  folder: LibraryFolderRow;
  depth: number;
}) {
  const hasChildren = folders.value.some((f) => f.parentId === folder.id);
  const isExpanded = expanded.value.has(folder.id);
  const isSelected = selectedFolderId.value === folder.id;

  function toggle(e: MouseEvent) {
    e.stopPropagation();
    const next = new Set(expanded.value);
    if (next.has(folder.id)) next.delete(folder.id);
    else next.add(folder.id);
    expanded.value = next;
  }
  function select() {
    // No modo de painel duplo, clique na árvore navega o painel ATIVO.
    // [[project-library-explorer-split-pane]]
    if (splitMode.value && activePane.value === "secondary") {
      selectedFolderIdB.value = folder.id;
    } else {
      selectedFolderId.value = folder.id;
    }
    // Auto-expande quando seleciona.
    if (hasChildren && !isExpanded) {
      const next = new Set(expanded.value);
      next.add(folder.id);
      expanded.value = next;
    }
  }

  return (
    <li>
      <div
        draggable={true}
        onDragStart={(e) => onFolderDragStart(e, folder.id)}
        onDragOver={(e) => onFolderDragOver(e, folder.id)}
        onDragLeave={onFolderDragLeave}
        onDrop={(e) => void onFolderDrop(e, folder.id)}
        class={`group flex items-center gap-1 rounded-md px-1.5 py-1 text-sm transition-colors data-[drop-over=true]:bg-primary/20 data-[drop-over=true]:outline data-[drop-over=true]:outline-2 data-[drop-over=true]:outline-primary ${
          isSelected ? "bg-primary/10 font-semibold text-primary" : "hover:bg-accent"
        }`}
        style={{ paddingLeft: `${0.5 + depth * 1}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={toggle}
            class="grid size-4 shrink-0 place-items-center text-muted-foreground hover:text-foreground"
            aria-label={isExpanded ? "Recolher" : "Expandir"}
          >
            <ChevronRight
              className={`size-3 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            />
          </button>
        ) : (
          <span class="size-4 shrink-0" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={select}
          class="flex flex-1 items-center gap-1.5 truncate text-left"
        >
          {isExpanded ? (
            <FolderOpen className="size-4 shrink-0 text-amber-500" />
          ) : (
            <Folder className="size-4 shrink-0 text-amber-500" />
          )}
          <span class="flex-1 truncate">{folder.name}</span>
          <span
            class="shrink-0 rounded bg-muted px-1.5 font-mono text-[10px] text-muted-foreground"
            title={
              hasChildren
                ? `${folder.bankCountTotal} banco(s) + ${folder.essayCountTotal} redação(ões) direto · ${countRecursive(folder.id) - folder.bankCountTotal - folder.essayCountTotal} em sub-pastas`
                : `${folder.bankCountTotal} banco(s) + ${folder.essayCountTotal} redação(ões)`
            }
          >
            {countRecursive(folder.id)}
          </span>
        </button>
      </div>
      {hasChildren && isExpanded && (
        <FolderTreeNode parentId={folder.id} depth={depth + 1} />
      )}
    </li>
  );
}

// ============================================================================
// Painel direito: conteúdo da pasta selecionada
// ============================================================================

function FolderContentPanel() {
  const sel = selectedFolderId.value;
  if (sel === null) {
    return <RootContent />;
  }
  if (sel === "none") {
    return <OrphansContent />;
  }
  const folder = folders.value.find((f) => f.id === sel);
  if (!folder) {
    return (
      <section class="rounded-md border border-border bg-card/40 p-6 text-sm text-muted-foreground">
        Pasta não encontrada. Recarregue.
      </section>
    );
  }
  return <FolderContent folder={folder} />;
}

// ============================================================================
// RootContent — quando nada está selecionado, mostra grid das pastas-raiz
// na área direita (como abrir o "Este computador" do File Explorer).
// ============================================================================

function RootContent() {
  const roots = folders.value
    .filter((f) => !f.parentId)
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));

  return (
    <section class="flex min-h-[400px] flex-col rounded-md border border-border bg-card/40">
      <header class="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div>
          <Breadcrumb folder={null} />
          <h2 class="mt-1 flex items-center gap-2 text-base font-bold">
            <Library className="size-5 text-muted-foreground" />
            Biblioteca
          </h2>
        </div>
        <button
          type="button"
          onClick={() => void createFolder(null)}
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
        >
          <FolderPlus className="size-3.5" />
          Nova pasta
        </button>
      </header>

      <div class="flex-1 p-4">
        {roots.length === 0 ? (
          <div class="grid h-full place-items-center text-center">
            <div>
              <Library className="mx-auto mb-2 size-10 text-muted-foreground/40" />
              <p class="text-sm font-semibold">Biblioteca vazia</p>
              <p class="mt-1 text-xs text-muted-foreground">
                Crie a primeira pasta para começar a organizar.
              </p>
              <button
                type="button"
                onClick={() => void createFolder(null)}
                class="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:brightness-110"
              >
                <FolderPlus className="size-3.5" />
                Nova pasta
              </button>
            </div>
          </div>
        ) : (
          <ul class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {roots.map((f) => (
              <FolderCard key={f.id} folder={f} />
            ))}
            <li>
              <button
                type="button"
                onClick={() => (selectedFolderId.value = "none")}
                class="flex h-full w-full items-center gap-2 rounded-md border border-dashed border-amber-500/40 bg-amber-500/5 px-3 py-3 text-left text-sm hover:bg-amber-500/10"
              >
                <Inbox className="size-5 text-amber-600 dark:text-amber-400" />
                <div class="min-w-0 flex-1">
                  <div class="font-semibold text-amber-800 dark:text-amber-200">Sem pasta (órfãos)</div>
                  <div class="text-[11px] text-amber-700/80 dark:text-amber-300/80">
                    Bancos sem categoria
                  </div>
                </div>
              </button>
            </li>
          </ul>
        )}
      </div>
    </section>
  );
}

function FolderCard({ folder }: { folder: LibraryFolderRow }) {
  const subCount = folders.value.filter((f) => f.parentId === folder.id).length;
  return (
    <li
      draggable={true}
      onDragStart={(e) => onFolderDragStart(e, folder.id)}
      onDragOver={(e) => onFolderDragOver(e, folder.id)}
      onDragLeave={onFolderDragLeave}
      onDrop={(e) => void onFolderDrop(e, folder.id)}
      data-drop-target-folder
    >
      <button
        type="button"
        onClick={() => (selectedFolderId.value = folder.id)}
        class="group flex h-full w-full items-center gap-2 rounded-md border border-border bg-background px-3 py-3 text-left transition-all hover:-translate-y-px hover:border-primary/40 hover:bg-accent/40 hover:shadow-sm"
      >
        <Folder className="size-5 shrink-0 text-amber-500" />
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold">{folder.name}</div>
          <div class="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
            <span class="rounded bg-muted px-1 font-mono">{folder.bankCountTotal} banco(s)</span>
            {folder.essayCountTotal > 0 && (
              <span class="rounded bg-violet-500/15 px-1 font-mono text-violet-700 dark:text-violet-300">
                {folder.essayCountTotal} redação(ões)
              </span>
            )}
            {subCount > 0 && (
              <span class="rounded bg-muted px-1 font-mono">{subCount} subpasta(s)</span>
            )}
          </div>
        </div>
        <ChevronRight className="size-4 text-muted-foreground/60 transition-all group-hover:text-primary group-hover:translate-x-0.5" />
      </button>
    </li>
  );
}

// ============================================================================
// Drag & Drop nativo (HTML5). Dois tipos de payload no dataTransfer:
//   - "application/x-library-folder"  —  ID de pasta sendo movida
//   - "application/x-library-bank"    —  JSON.stringify(bankIds[]) — suporta
//                                       arrastar 1 ou N bancos selecionados
// Drop targets aceitam ambos os tipos; a função descobre qual é e despacha.
// ============================================================================

const MIME_FOLDER = "application/x-library-folder";
const MIME_BANK = "application/x-library-bank";

function onFolderDragStart(e: DragEvent, folderId: string) {
  if (!e.dataTransfer) return;
  e.dataTransfer.setData(MIME_FOLDER, folderId);
  e.dataTransfer.effectAllowed = "move";
}

/** Drag de banco. Se `selectedIds` tiver mais de um e incluir o id, arrasta
 *  todos os selecionados; senão arrasta só este. */
function onBankDragStart(e: DragEvent, bankId: string, selectedIds: Set<string>) {
  if (!e.dataTransfer) return;
  const ids =
    selectedIds.size > 1 && selectedIds.has(bankId)
      ? Array.from(selectedIds)
      : [bankId];
  e.dataTransfer.setData(MIME_BANK, JSON.stringify(ids));
  e.dataTransfer.effectAllowed = "move";
}

function onFolderDragOver(e: DragEvent, _targetId: string | "root") {
  const types = e.dataTransfer?.types;
  if (!types) return;
  if (!types.includes(MIME_FOLDER) && !types.includes(MIME_BANK)) return;
  e.preventDefault();
  e.dataTransfer!.dropEffect = "move";
  (e.currentTarget as HTMLElement)?.setAttribute("data-drop-over", "true");
}

function onFolderDragLeave(e: DragEvent) {
  (e.currentTarget as HTMLElement)?.removeAttribute("data-drop-over");
}

async function onFolderDrop(e: DragEvent, targetId: string | null) {
  e.preventDefault();
  (e.currentTarget as HTMLElement)?.removeAttribute("data-drop-over");
  const dt = e.dataTransfer;
  if (!dt) return;

  // 1) Drop de bancos
  const banksJson = dt.getData(MIME_BANK);
  if (banksJson) {
    let ids: string[] = [];
    try { ids = JSON.parse(banksJson); } catch { return; }
    if (!Array.isArray(ids) || ids.length === 0) return;
    // Captura o libraryFolderId atual de cada banco para registrar no undo.
    const fromMap: Record<string, string | null> = {};
    for (const id of ids) {
      const b = banks.value.find((x) => x.id === id);
      fromMap[id] = b?.libraryFolderId ?? null;
    }
    try {
      await api.bulkAssignLibraryBanks(targetId, ids);
      const targetName =
        targetId === null
          ? "Sem pasta"
          : folders.value.find((f) => f.id === targetId)?.name ?? "pasta";
      pushUndo({
        kind: "bank-move",
        fromMap,
        toFolderId: targetId,
        summary: `${ids.length} banco(s) —  "${targetName}"`
      });
      await loadFolders();
      const sel = selectedFolderId.value;
      if (sel === "none") await loadBanksForFolder(null);
      else if (sel) await loadBanksForFolder(sel);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err) });
    }
    return;
  }

  // 2) Drop de pasta
  const sourceId = dt.getData(MIME_FOLDER);
  if (!sourceId) return;
  if (sourceId === targetId) return;
  if (targetId && isDescendant(sourceId, targetId)) {
    void openAlert({
      title: "Movimento inválido",
      message: "Você não pode mover uma pasta para dentro de uma sub-pasta dela mesma."
    });
    return;
  }
  const source = folders.value.find((f) => f.id === sourceId);
  const fromParentId = source?.parentId ?? null;
  try {
    await api.patchLibraryFolder(sourceId, { parentId: targetId });
    const targetName =
      targetId === null
        ? "raiz"
        : folders.value.find((f) => f.id === targetId)?.name ?? "pasta";
    pushUndo({
      kind: "folder-move",
      folderId: sourceId,
      fromParentId,
      toParentId: targetId,
      summary: `Pasta "${source?.name ?? "?"}" —  "${targetName}"`
    });
    if (targetId) {
      const next = new Set(expanded.value);
      next.add(targetId);
      expanded.value = next;
    }
    await loadFolders();
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err) });
  }
}

/** Soma o bankCountTotal da pasta + todos os descendentes. Pedido 2026-05-19:
 *  contador da pasta deve incluir bancos das sub-pastas. */
function countRecursive(folderId: string): number {
  const f = folders.value.find((f) => f.id === folderId);
  const direct = (f?.bankCountTotal ?? 0) + (f?.essayCountTotal ?? 0);
  const children = folders.value.filter((f) => f.parentId === folderId);
  let sum = direct;
  for (const c of children) sum += countRecursive(c.id);
  return sum;
}

function isDescendant(ancestorId: string, candidateId: string): boolean {
  // candidateId é descendente de ancestorId?
  let cur: LibraryFolderRow | undefined = folders.value.find((f) => f.id === candidateId);
  const safety = new Set<string>();
  while (cur && cur.parentId && !safety.has(cur.id)) {
    safety.add(cur.id);
    if (cur.parentId === ancestorId) return true;
    cur = folders.value.find((f) => f.id === cur!.parentId);
  }
  return false;
}

function Breadcrumb({ folder }: { folder: LibraryFolderRow | null }) {
  // Caminho ancestral
  const trail: LibraryFolderRow[] = [];
  let cur = folder;
  const safety = new Set<string>();
  while (cur && !safety.has(cur.id)) {
    trail.unshift(cur);
    safety.add(cur.id);
    cur = cur.parentId ? folders.value.find((x) => x.id === cur!.parentId) ?? null : null;
  }
  // Wrapper com separador inferior + spacing — evita o "colado" entre o path
  // de pastas e o título grande do header logo abaixo. Ícone Route à esquerda
  // sinaliza "este bloco é o caminho até aqui".
  return (
    <nav
      aria-label="Caminho de pastas"
      class="-mx-2 mb-2 flex flex-wrap items-center gap-1 border-b border-border/70 px-2 pb-2 text-xs text-muted-foreground"
    >
      <Route className="size-3 shrink-0 text-muted-foreground/70" aria-hidden />
      <button
        type="button"
        onClick={() => (selectedFolderId.value = null)}
        class="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-accent hover:text-foreground"
      >
        <Library className="size-3" />
        Biblioteca
      </button>
      {trail.map((f, i) => (
        <span key={f.id} class="inline-flex items-center gap-1">
          <ChevronRight className="size-3 text-muted-foreground/60" />
          <button
            type="button"
            onClick={() => (selectedFolderId.value = f.id)}
            class={`rounded px-1.5 py-0.5 hover:bg-accent hover:text-foreground ${
              i === trail.length - 1 ? "font-semibold text-foreground" : ""
            }`}
          >
            {f.name}
          </button>
        </span>
      ))}
    </nav>
  );
}

function FolderContent({ folder }: { folder: LibraryFolderRow }) {
  const childFolders = folders.value
    .filter((f) => f.parentId === folder.id)
    .sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [picker, setPicker] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = window.localStorage.getItem("library.viewMode");
    return saved === "grid" ? "grid" : "list";
  });
  function changeView(m: ViewMode) {
    setViewMode(m);
    try { window.localStorage.setItem("library.viewMode", m); } catch {}
  }

  // Reseta seleção ao trocar de pasta.
  useEffect(() => {
    setSelected(new Set());
  }, [folder.id]);

  function toggleBank(id: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  }
  function toggleAll(checked: boolean) {
    if (!checked) setSelected(new Set());
    else setSelected(new Set(banks.value.map((b) => b.id)));
  }

  async function doRename() {
    const name = await openPrompt({
      title: "Renomear pasta",
      message: "Novo nome:",
      defaultValue: folder.name,
      maxLength: 100,
      confirmLabel: "Salvar"
    });
    if (!name || name === folder.name) return;
    const oldName = folder.name;
    try {
      await api.patchLibraryFolder(folder.id, { name: name.trim() });
      pushUndo({
        kind: "folder-rename",
        folderId: folder.id,
        oldName,
        newName: name.trim(),
        summary: `Renomeou "${oldName}" —  "${name.trim()}"`
      });
      await loadFolders();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  async function doDelete() {
    const ok = await openConfirm({
      title: `Apagar "${folder.name}"?`,
      message:
        folder.bankCountTotal > 0
          ? `${folder.bankCountTotal} banco(s) dentro vão para "Sem pasta".`
          : "A pasta será removida.",
      confirmLabel: "Apagar",
      danger: true
    });
    if (!ok) return;
    try {
      await api.deleteLibraryFolder(folder.id);
      selectedFolderId.value = folder.parentId ?? null;
      await loadFolders();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }

  return (
    <section class="flex min-h-[400px] flex-col rounded-md border border-border bg-card/40">
      <header class="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div class="min-w-0 flex-1">
          <Breadcrumb folder={folder} />
          <div class="mt-1 flex items-center gap-2">
            <h2 class="flex items-center gap-2 truncate text-base font-bold">
              <FolderOpen className="size-5 text-amber-500" />
              {folder.name}
            </h2>
            <VisibilityChip value={folder.defaultVisibility} />
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => void createFolder(folder.id)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
            title="Nova subpasta aqui"
          >
            <FolderPlus className="size-3.5" />
            Subpasta
          </button>
          <button
            type="button"
            onClick={() => setPicker(true)}
            class="inline-flex items-center gap-1 rounded-md bg-primary/90 px-2 py-1 text-xs font-bold text-primary-foreground hover:bg-primary"
          >
            <Plus className="size-3.5" />
            Adicionar bancos
          </button>
          <button
            type="button"
            onClick={() => void doRename()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent"
            title="Renomear"
          >
            <Edit3 className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void doDelete()}
            class="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
            title="Apagar pasta"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </header>

      {/* Sub-pastas */}
      {childFolders.length > 0 && (
        <div class="border-b border-border px-4 py-3">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Subpastas ({childFolders.length})
          </div>
          <div class="flex flex-wrap gap-1.5">
            {childFolders.map((c) => (
              <div
                key={c.id}
                draggable={true}
                onDragStart={(e) => onFolderDragStart(e, c.id)}
                onDragOver={(e) => onFolderDragOver(e, c.id)}
                onDragLeave={onFolderDragLeave}
                onDrop={(e) => void onFolderDrop(e, c.id)}
                class="rounded-md data-[drop-over=true]:outline data-[drop-over=true]:outline-2 data-[drop-over=true]:outline-primary"
              >
                <button
                  type="button"
                  onClick={() => (selectedFolderId.value = c.id)}
                  class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent"
                >
                  <Folder className="size-3.5 text-amber-500" />
                  {c.name}
                  <span
                    class="rounded bg-muted px-1 font-mono text-[10px] text-muted-foreground"
                    title={`${c.bankCountTotal} banco(s) + ${c.essayCountTotal} redação(ões)`}
                  >
                    {c.bankCountTotal + c.essayCountTotal}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Banks toolbar + lista */}
      <div class="flex flex-1 flex-col">
        <BanksToolbar
          totalSelected={selected.size}
          totalShown={banks.value.length}
          onSelectAll={(c) => toggleAll(c)}
          onMove={() => setMoveOpen(true)}
          onClear={() => setSelected(new Set())}
          viewMode={viewMode}
          onViewModeChange={changeView}
        />
        {banksPhase.value === "loading" && (
          <p class="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Carregando bancos⬦
          </p>
        )}
        {banksPhase.value === "error" && (
          <p class="px-4 py-6 text-sm text-destructive">
            <AlertCircle className="mr-1 inline size-3" />
            {banksErr.value}
          </p>
        )}
        {banksPhase.value === "ready" &&
          banks.value.length === 0 &&
          essays.value.length === 0 && (
            <div class="grid flex-1 place-items-center px-4 py-12 text-center">
              <div>
                <FileText className="mx-auto mb-2 size-8 text-muted-foreground/40" />
                <p class="text-sm text-muted-foreground">
                  Pasta vazia. Use{" "}
                  <button
                    type="button"
                    onClick={() => setPicker(true)}
                    class="font-semibold text-primary underline"
                  >
                    Adicionar bancos
                  </button>{" "}
                  para incluir (ou edite uma redação em "Biblioteca de Redação"
                  e atribua esta pasta).
                </p>
              </div>
            </div>
          )}
        {banksPhase.value === "ready" && banks.value.length > 0 && (
          <BanksList
            banks={banks.value}
            selected={selected}
            onToggle={toggleBank}
            viewMode={viewMode}
          />
        )}
        {/* Seção de redações da pasta. Aparece sempre que a pasta tem alguma
            (não importa se tem bancos ou não). */}
        <FolderEssaysList essays={essays.value} phase={essaysPhase.value} />
      </div>

      {picker && (
        <BankPickerModal
          targetFolderId={folder.id}
          targetFolderName={folder.name}
          onClose={() => setPicker(false)}
          onAssigned={async () => {
            setPicker(false);
            await Promise.all([loadFolders(), loadBanksForFolder(folder.id)]);
          }}
        />
      )}
      {moveOpen && selected.size > 0 && (
        <MoveBanksModal
          bankIds={Array.from(selected)}
          fromFolder={folder}
          onClose={() => setMoveOpen(false)}
          onMoved={async () => {
            setMoveOpen(false);
            setSelected(new Set());
            await Promise.all([loadFolders(), loadBanksForFolder(folder.id)]);
          }}
        />
      )}
    </section>
  );
}

// ============================================================================
// Pasta virtual: "Sem pasta" (órfãos). Filtro "Só públicos".
// ============================================================================

function OrphansContent() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = window.localStorage.getItem("library.viewMode");
    return saved === "grid" ? "grid" : "list";
  });
  function changeView(m: ViewMode) {
    setViewMode(m);
    try { window.localStorage.setItem("library.viewMode", m); } catch {}
  }

  useEffect(() => {
    setSelected(new Set());
    onlyPublic.value = false;
  }, []);

  const filtered = useMemo(() => {
    if (onlyPublic.value) return banks.value.filter((b) => b.visibility === "public");
    return banks.value;
  }, [banks.value, onlyPublic.value]);

  function toggleBank(id: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(id);
    else next.delete(id);
    setSelected(next);
  }
  function toggleAll(checked: boolean) {
    if (!checked) setSelected(new Set());
    else setSelected(new Set(filtered.map((b) => b.id)));
  }

  return (
    <section class="flex min-h-[400px] flex-col rounded-md border border-amber-500/30 bg-amber-500/5">
      <header class="flex flex-wrap items-center justify-between gap-3 border-b border-amber-500/20 px-4 py-2.5">
        <div>
          <Breadcrumb folder={null} />
          <h2 class="mt-1 flex items-center gap-2 text-base font-bold text-amber-800 dark:text-amber-200">
            <Inbox className="size-5" />
            Sem pasta (órfãos)
          </h2>
          <p class="mt-0.5 text-[11px] text-amber-700/80 dark:text-amber-300/80">
            Bancos que ainda não foram categorizados. Selecione e mova para uma pasta.
          </p>
        </div>
        <label
          class={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
            onlyPublic.value
              ? "border-violet-500/50 bg-violet-500/10 text-violet-700 dark:text-violet-300"
              : "border-border bg-background text-muted-foreground hover:bg-accent"
          }`}
        >
          <input
            type="checkbox"
            class="hidden"
            checked={onlyPublic.value}
            onChange={(e) => (onlyPublic.value = (e.target as HTMLInputElement).checked)}
          />
          <Filter className="size-3.5" />
          Só públicos
        </label>
      </header>

      <BanksToolbar
        totalSelected={selected.size}
        totalShown={filtered.length}
        onSelectAll={(c) => toggleAll(c)}
        onMove={() => setMoveOpen(true)}
        onClear={() => setSelected(new Set())}
        viewMode={viewMode}
        onViewModeChange={changeView}
      />

      {banksPhase.value === "loading" && (
        <p class="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando⬦
        </p>
      )}
      {banksPhase.value === "ready" && filtered.length === 0 && (
        <div class="grid flex-1 place-items-center px-4 py-12 text-center">
          <div>
            <Inbox className="mx-auto mb-2 size-8 text-muted-foreground/40" />
            <p class="text-sm text-muted-foreground">
              {onlyPublic.value
                ? "Nenhum banco público sem pasta. 🚫"
                : "Nenhum banco órfão."}
            </p>
          </div>
        </div>
      )}
      {banksPhase.value === "ready" && filtered.length > 0 && (
        <BanksList
          banks={filtered}
          selected={selected}
          onToggle={toggleBank}
          viewMode={viewMode}
        />
      )}

      {moveOpen && selected.size > 0 && (
        <MoveBanksModal
          bankIds={Array.from(selected)}
          fromFolder={null}
          onClose={() => setMoveOpen(false)}
          onMoved={async () => {
            setMoveOpen(false);
            setSelected(new Set());
            await Promise.all([loadFolders(), loadBanksForFolder(null)]);
          }}
        />
      )}
    </section>
  );
}

// ============================================================================
// Toolbar comum (multi-select)
// ============================================================================

function BanksToolbar({
  totalSelected,
  totalShown,
  onSelectAll,
  onMove,
  onClear,
  viewMode,
  onViewModeChange
}: {
  totalSelected: number;
  totalShown: number;
  onSelectAll: (checked: boolean) => void;
  onMove: () => void;
  onClear: () => void;
  viewMode?: ViewMode;
  onViewModeChange?: (m: ViewMode) => void;
}) {
  const allChecked = totalShown > 0 && totalSelected === totalShown;
  return (
    <div class="flex items-center justify-between gap-2 border-b border-border px-4 py-2 text-xs">
      <label class="inline-flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={allChecked}
          onChange={(e) => onSelectAll((e.target as HTMLInputElement).checked)}
        />
        <span class="font-semibold text-muted-foreground">
          {totalSelected > 0 ? `${totalSelected} selecionado(s)` : `${totalShown} banco(s)`}
        </span>
      </label>
      <div class="flex items-center gap-1.5">
        {totalSelected > 0 && (
          <>
            <button
              type="button"
              onClick={onMove}
              class="inline-flex items-center gap-1 rounded-md bg-primary/90 px-2 py-1 text-[11px] font-bold text-primary-foreground hover:bg-primary"
            >
              <Move className="size-3.5" />
              Mover {totalSelected} para⬦
            </button>
            <button
              type="button"
              onClick={onClear}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
            >
              <X className="size-3" />
              Limpar
            </button>
          </>
        )}
        {onViewModeChange && (
          <div class="inline-flex overflow-hidden rounded-md border border-border">
            <button
              type="button"
              onClick={() => onViewModeChange("list")}
              class={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold transition-colors ${
                viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
              }`}
              title="Visualizar em lista"
              {...(viewMode === "list" ? { "aria-pressed": "true" as const } : { "aria-pressed": "false" as const })}
            >
              <List className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("grid")}
              class={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold transition-colors ${
                viewMode === "grid" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-accent"
              }`}
              title="Visualizar em grade"
              {...(viewMode === "grid" ? { "aria-pressed": "true" as const } : { "aria-pressed": "false" as const })}
            >
              <Grid3x3 className="size-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Lista de bancos (compartilhada entre pasta normal e órfãos)
// ============================================================================

type ViewMode = "list" | "grid";

/** Lista compacta de redações dentro da pasta atual. Aparece logo abaixo da
 *  BanksList. Não tem checkbox/seleção/move por enquanto — o admin gerencia
 *  redações via "Biblioteca de Redação" no menu (atribui pasta no Edit).
 *  Loading state silencioso (só aparece quando tem essays). */
function FolderEssaysList({
  essays: list,
  phase
}: {
  essays: EssayInFolder[];
  phase: "loading" | "ready" | "error" | "idle";
}) {
  if (phase !== "ready" || list.length === 0) return null;
  return (
    <div class="border-t border-border bg-violet-500/5">
      <div class="flex items-center gap-2 border-b border-violet-500/15 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
        <FilePenLine className="size-3.5" />
        Redações nesta pasta
        <span class="rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-bold tabular-nums text-violet-700 dark:text-violet-300">
          {list.length}
        </span>
        <span class="ml-auto text-[10px] font-normal text-muted-foreground normal-case tracking-normal">
          Edite atribuição de pasta em <strong>Biblioteca de Redação</strong>
        </span>
      </div>
      <ul class="divide-y divide-violet-500/15">
        {list.map((e) => (
          <li
            key={e.id}
            class="flex items-center gap-3 px-4 py-2 text-sm"
          >
            <FilePenLine className="size-3.5 text-violet-500 shrink-0" />
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate font-semibold">
                  {e.displayName || e.title}
                </span>
                <span class="shrink-0 rounded bg-violet-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                  {e.banca}
                </span>
                {e.year != null && (
                  <span class="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9px] font-mono text-muted-foreground">
                    {e.year}
                  </span>
                )}
                {e.phase && (
                  <span class="shrink-0 text-[10px] text-muted-foreground">
                    {e.phase}
                  </span>
                )}
              </div>
              {e.edition && (
                <div class="truncate text-[11px] text-muted-foreground">
                  {e.edition}
                </div>
              )}
            </div>
            <VisibilityChip value={e.visibility} />
            <span class="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[9px] font-mono uppercase text-muted-foreground">
              {e.status}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BanksList({
  banks,
  selected,
  onToggle,
  viewMode = "list"
}: {
  banks: BankInFolder[];
  selected: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  viewMode?: ViewMode;
}) {
  if (viewMode === "grid") {
    return (
      <ul class="grid grid-cols-1 gap-2 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {banks.map((b) => (
          <BankGridCard
            key={b.id}
            bank={b}
            selected={selected.has(b.id)}
            selectedIds={selected}
            onToggle={onToggle}
          />
        ))}
      </ul>
    );
  }
  return (
    <ul class="divide-y divide-border">
      {banks.map((b) => {
        const isSel = selected.has(b.id);
        return (
          <li
            key={b.id}
            draggable={true}
            onDragStart={(e) => onBankDragStart(e, b.id, selected)}
            class={`flex cursor-grab items-center gap-2 px-4 py-2 text-sm transition-colors active:cursor-grabbing ${
              isSel ? "bg-primary/5" : "hover:bg-accent/40"
            }`}
            title="Arraste para uma pasta na árvore ou clique para selecionar"
          >
            <input
              type="checkbox"
              checked={isSel}
              onChange={(e) => onToggle(b.id, (e.target as HTMLInputElement).checked)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Selecionar banco ${b.name}`}
            />
            <VisibilityEye value={b.visibility} />
            <FileText className="size-4 shrink-0 text-muted-foreground" />
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="truncate font-semibold">{b.name}</span>
                <VisibilityChip value={b.visibility} size="xs" />
                {b.archivedAt && (
                  <span class="rounded bg-muted px-1.5 py-0 text-[10px] text-muted-foreground">
                    arquivado
                  </span>
                )}
              </div>
              {(() => {
                // Banco público da Biblioteca não exibe organização — admin
                // pediu pra não vazar associação org→banco quando o banco
                // está exposto publicamente. [[project-biblioteca-de-provas]]
                const showCompany = b.visibility !== "public" && b.companyName;
                if (!showCompany && !b.description) return null;
                return (
                  <p class="truncate text-[11px] text-muted-foreground">
                    {showCompany && <span class="font-mono">{b.companyName}</span>}
                    {showCompany && b.description && " · "}
                    {b.description}
                  </p>
                );
              })()}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function BankGridCard({
  bank,
  selected,
  selectedIds,
  onToggle
}: {
  bank: BankInFolder;
  selected: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
}) {
  return (
    <li
      draggable={true}
      onDragStart={(e) => onBankDragStart(e, bank.id, selectedIds)}
      class={`group relative flex cursor-grab flex-col items-center gap-1.5 rounded-md border p-3 text-center transition-all active:cursor-grabbing ${
        selected ? "border-primary bg-primary/5" : "border-border bg-background hover:border-primary/40 hover:shadow-sm"
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={(e) => onToggle(bank.id, (e.target as HTMLInputElement).checked)}
        onClick={(e) => e.stopPropagation()}
        class="absolute left-2 top-2"
        aria-label={`Selecionar banco ${bank.name}`}
      />
      <span class="absolute right-2 top-2">
        <VisibilityEye value={bank.visibility} />
      </span>
      <FileText className="size-10 text-muted-foreground/70 group-hover:text-primary/70" />
      <div class="min-w-0 w-full">
        <div class="line-clamp-2 break-words text-xs font-semibold">{bank.name}</div>
        <div class="mt-1 flex items-center justify-center gap-1">
          <VisibilityChip value={bank.visibility} size="xs" />
        </div>
        {bank.companyName && bank.visibility !== "public" && (
          <div class="mt-0.5 truncate text-[10px] text-muted-foreground">{bank.companyName}</div>
        )}
      </div>
    </li>
  );
}

// ============================================================================
// Visibility chip
// ============================================================================

/** Olhinho de visibilidade — confirmação rápida se o banco aparece OU NÃO
 *  na Biblioteca de Provas pública. Verde = público (sai na biblioteca);
 *  cinza riscado = privado/unlisted (NÃO sai). Pedido 2026-05-19: validar
 *  visualmente sem ter que abrir o banco. */
function VisibilityEye({ value }: { value: "private" | "unlisted" | "public" | string }) {
  const isPublic = value === "public";
  return (
    <span
      class={`inline-flex size-5 shrink-0 items-center justify-center rounded ${
        isPublic
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
          : "bg-muted text-muted-foreground"
      }`}
      title={
        isPublic
          ? "Visível na Biblioteca de provas (público)"
          : value === "unlisted"
          ? "NÃO listado na biblioteca — só acessível por link"
          : "Privado — não aparece na biblioteca"
      }
      aria-label={
        isPublic
          ? "Visível na biblioteca"
          : "Não visível na biblioteca"
      }
    >
      {isPublic ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
    </span>
  );
}

function VisibilityChip({
  value,
  size = "sm"
}: {
  value: "private" | "unlisted" | "public" | string;
  size?: "xs" | "sm";
}) {
  const pad = size === "xs" ? "px-1 py-0 text-[9px]" : "px-1.5 py-0.5 text-[10px]";
  if (value === "public") {
    return (
      <span class={`inline-flex items-center gap-0.5 rounded-md bg-violet-500/15 ${pad} font-semibold text-violet-700 dark:text-violet-300`}>
        <Globe className="size-2.5" />
        Público
      </span>
    );
  }
  if (value === "unlisted") {
    return (
      <span class={`inline-flex items-center rounded-md bg-amber-500/15 ${pad} font-semibold text-amber-700 dark:text-amber-300`}>
        Não listado
      </span>
    );
  }
  return (
    <span class={`inline-flex items-center gap-0.5 rounded-md bg-muted ${pad} font-semibold text-muted-foreground`}>
      <Lock className="size-2.5" />
      Privado
    </span>
  );
}

// ============================================================================
// Modal: "Adicionar bancos a esta pasta" (picker filtrável)
// ============================================================================

type PickerBank = {
  id: string;
  name: string;
  companyName: string | null;
  visibility: "private" | "unlisted" | "public";
  libraryFolderId: string | null;
  archivedAt: string | null;
};

function BankPickerModal({
  targetFolderId,
  targetFolderName,
  onClose,
  onAssigned
}: {
  targetFolderId: string;
  targetFolderName: string;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [allBanks, setAllBanks] = useState<PickerBank[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [hideAssigned, setHideAssigned] = useState(true);
  const [hidePrivate, setHidePrivate] = useState(false);
  const [hideArchived, setHideArchived] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.listAllBanksForLibrary();
        setAllBanks(r.banks);
      } catch (e) {
        setErr(friendlyError(e));
      } finally {
        setLoadingList(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return allBanks.filter((b) => {
      if (hideAssigned && b.libraryFolderId && b.libraryFolderId !== targetFolderId) return false;
      if (b.libraryFolderId === targetFolderId) return false; // já nesta pasta
      if (hidePrivate && b.visibility === "private") return false;
      if (hideArchived && b.archivedAt) return false;
      if (term && !b.name.toLowerCase().includes(term) && !(b.companyName?.toLowerCase().includes(term))) {
        return false;
      }
      return true;
    });
  }, [allBanks, q, hideAssigned, hidePrivate, hideArchived, targetFolderId]);

  function toggle(id: string) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  }

  async function save() {
    if (picked.size === 0) return;
    setSaving(true);
    try {
      await api.bulkAssignLibraryBanks(targetFolderId, Array.from(picked));
      onAssigned();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="flex h-[85vh] max-h-[800px] w-full max-w-3xl flex-col rounded-lg border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-2 border-b border-border px-5 py-3">
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Adicionar bancos
            </div>
            <div class="flex items-center gap-1.5 text-base font-bold">
              <ArrowRight className="size-4 text-muted-foreground" />
              <FolderOpen className="size-4 text-amber-500" />
              {targetFolderName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex flex-wrap items-center gap-2 border-b border-border px-5 py-2">
          <div class="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Buscar banco ou organização⬦"
              value={q}
              onInput={(e) => setQ((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-sm outline-none focus:border-primary"
            />
          </div>
          <label class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
            <input
              type="checkbox"
              checked={hideAssigned}
              onChange={(e) => setHideAssigned((e.target as HTMLInputElement).checked)}
            />
            Esconder já categorizados
          </label>
          <label class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
            <input
              type="checkbox"
              checked={hidePrivate}
              onChange={(e) => setHidePrivate((e.target as HTMLInputElement).checked)}
            />
            Esconder privados
          </label>
          <label class="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
            <input
              type="checkbox"
              checked={hideArchived}
              onChange={(e) => setHideArchived((e.target as HTMLInputElement).checked)}
            />
            Esconder arquivados
          </label>
        </div>

        <div class="flex-1 overflow-y-auto">
          {loadingList && (
            <p class="flex items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Carregando⬦
            </p>
          )}
          {err && (
            <p class="px-4 py-6 text-sm text-destructive">
              <AlertCircle className="mr-1 inline size-3" />
              {err}
            </p>
          )}
          {!loadingList && filtered.length === 0 && (
            <p class="px-4 py-12 text-center text-sm text-muted-foreground">
              Nenhum banco corresponde aos filtros.
            </p>
          )}
          {filtered.length > 0 && (
            <ul class="divide-y divide-border">
              {filtered.map((b) => {
                const isPicked = picked.has(b.id);
                return (
                  <li
                    key={b.id}
                    onClick={() => toggle(b.id)}
                    class={`flex cursor-pointer items-center gap-2 px-4 py-2 text-sm transition-colors ${
                      isPicked ? "bg-primary/10" : "hover:bg-accent/40"
                    }`}
                  >
                    <input type="checkbox" checked={isPicked} readOnly />
                    <FileText className="size-4 text-muted-foreground" />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1.5">
                        <span class="truncate font-semibold">{b.name}</span>
                        <VisibilityChip value={b.visibility} size="xs" />
                        {b.libraryFolderId && b.libraryFolderId !== targetFolderId && (
                          <span class="rounded bg-amber-500/15 px-1 text-[9px] font-semibold text-amber-700 dark:text-amber-300">
                            já em outra pasta
                          </span>
                        )}
                      </div>
                      {b.companyName && b.visibility !== "public" && (
                        <p class="truncate text-[11px] text-muted-foreground">{b.companyName}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer class="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <span class="text-xs text-muted-foreground">
            {picked.size} selecionado(s)
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || picked.size === 0}
              class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Plus className="size-3.5" />
              )}
              Adicionar {picked.size > 0 ? `${picked.size}` : ""}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ============================================================================
// Modal: "Mover N bancos para⬦" (dropdown de pastas)
// ============================================================================

function MoveBanksModal({
  bankIds,
  fromFolder,
  onClose,
  onMoved
}: {
  bankIds: string[];
  fromFolder: LibraryFolderRow | null;
  onClose: () => void;
  onMoved: () => void;
}) {
  const [target, setTarget] = useState<string | "none">("none");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const folderId = target === "none" ? null : target;
      await api.bulkAssignLibraryBanks(folderId, bankIds);
      onMoved();
    } catch (e) {
      setErr(friendlyError(e));
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div class="w-full max-w-md rounded-lg border border-border bg-card p-5 shadow-xl">
        <header class="mb-3 flex items-center justify-between gap-2">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Move className="size-5 text-primary" />
            Mover {bankIds.length} banco(s)
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>
        <p class="mb-3 text-xs text-muted-foreground">
          De:{" "}
          <span class="font-mono">
            {fromFolder ? fromFolder.name : "Sem pasta (órfãos)"}
          </span>
        </p>
        <label class="block">
          <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Destino
          </span>
          <select
            value={target}
            onChange={(e) => setTarget((e.target as HTMLSelectElement).value as string)}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
          >
            <option value="none">📂 Sem pasta (órfãos — remove categorização)</option>
            {orderedHierarchy(folders.value)
              .filter(({ folder }) => !fromFolder || folder.id !== fromFolder.id)
              .map(({ folder, depth }) => (
                <option key={folder.id} value={folder.id}>
                  {"  ".repeat(depth)}
                  {folder.name}
                </option>
              ))}
          </select>
        </label>
        {err && (
          <p class="mt-2 text-xs text-destructive">
            <AlertCircle className="mr-1 inline size-3" /> {err}
          </p>
        )}
        <div class="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground hover:brightness-110 disabled:opacity-50"
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : <ArrowRight className="size-3.5" />}
            Mover
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

async function createFolder(parentId: string | null) {
  const name = await openPrompt({
    title: parentId ? "Nova subpasta" : "Nova pasta",
    message: "Nome da pasta — ex: \"Vestibular Medicina\", \"ENEM\".",
    placeholder: "Nome",
    maxLength: 80,
    confirmLabel: "Criar"
  });
  if (!name) return;
  try {
    const r = await api.createLibraryFolder({ name: name.trim(), parentId: parentId ?? undefined });
    pushUndo({
      kind: "folder-create",
      folderId: r.folder.id,
      summary: `Criou pasta "${name.trim()}"`
    });
    await loadFolders();
  } catch (err) {
    void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao criar." }) });
  }
}

function orderedHierarchy(
  list: LibraryFolderRow[]
): Array<{ folder: LibraryFolderRow; depth: number }> {
  const byParent = new Map<string | null, LibraryFolderRow[]>();
  for (const f of list) {
    const key = f.parentId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(f);
  }
  const out: Array<{ folder: LibraryFolderRow; depth: number }> = [];
  function walk(parentId: string | null, depth: number) {
    const children = byParent.get(parentId) ?? [];
    children.sort((a, b) => (a.position || 0) - (b.position || 0) || a.name.localeCompare(b.name));
    for (const c of children) {
      out.push({ folder: c, depth });
      walk(c.id, depth + 1);
    }
  }
  walk(null, 0);
  const seen = new Set(out.map((x) => x.folder.id));
  for (const f of list) if (!seen.has(f.id)) out.push({ folder: f, depth: 0 });
  return out;
}
